import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
  MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';
import { useAgent } from '../contexts/AgentContext';
import { useApi } from '../contexts/ApiContext';

const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{
      flexGrow: 1,
      display: value === index ? 'flex' : 'none',
      flexDirection: 'column'
    }}
  >
    {value === index && children}
  </Box>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const { currentAgent } = useAgent();
  const api = useApi();

  const agentIdentifier = currentAgent?.agent || user?.username || user?.id || '';

  const [crmTabs, setCrmTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [zoomPercent, setZoomPercent] = useState(100);

  const loadCrmSessions = useCallback(async () => {
    if (!agentIdentifier) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.agents.getCrmConfigs(agentIdentifier);
      const crmSkills = response?.data?.skills || [];
      if (!crmSkills.length) {
        setCrmTabs([]);
        setActiveTab(0);
        return;
      }

      const sessions = await Promise.all(crmSkills.map(async (skill) => {
        const baseInfo = {
          skillId: skill.id_skill,
          skillname: skill.skillname,
          skilldef: skill.skilldef,
          crmName: skill.crm_name
        };
        try {
          const sessionResponse = await api.agents.createCrmSession(agentIdentifier, skill.id_skill);
          const data = sessionResponse?.data || {};
          return {
            ...baseInfo,
            loginUrl: data.loginUrl,
            salesUrl: data.salesUrl,
            iframeSrc: data.loginUrl,
            stage: 'login',
            token: data.token
          };
        } catch (sessionError) {
          console.error('[CRM][SESSION]', sessionError);
          return {
            ...baseInfo,
            error: sessionError.message || 'No se pudo iniciar sesión en el CRM'
          };
        }
      }));

      setCrmTabs(sessions);
      setActiveTab(0);
    } catch (err) {
      console.error('[CRM][LOAD]', err);
      setError(err.message || 'No se pudo obtener la configuración de CRM');
      setCrmTabs([]);
    } finally {
      setLoading(false);
    }
  }, [agentIdentifier, api.agents]);

  useEffect(() => {
    if (!agentIdentifier) {
      setCrmTabs([]);
      return;
    }
    loadCrmSessions();
  }, [agentIdentifier, reloadKey, loadCrmSessions]);

  const handleIframeLoad = (skillId) => {
    setCrmTabs((prev) => {
      const idx = prev.findIndex((tab) => tab.skillId === skillId);
      if (idx === -1) return prev;
      const target = prev[idx];
      if (target.stage !== 'login') return prev;
      const updated = [...prev];
      updated[idx] = { ...target, stage: 'switching' };
      setTimeout(() => {
        setCrmTabs((current) => {
          const innerIdx = current.findIndex((tab) => tab.skillId === skillId);
          if (innerIdx === -1) return current;
          const currentTab = current[innerIdx];
          if (currentTab.stage !== 'switching') return current;
          const clone = [...current];
          clone[innerIdx] = {
            ...currentTab,
            stage: 'sales',
            iframeSrc: currentTab.salesUrl
          };
          return clone;
        });
      }, 600);
      return updated;
    });
  };

  const handleRefresh = () => {
    setReloadKey((prev) => prev + 1);
  };

  const hasCrmTabs = crmTabs && crmTabs.length > 0;

  const collapseDrawer = useCallback(() => {
    window.dispatchEvent(new Event('cti-close-drawer'));
  }, []);

  const zoomScale = zoomPercent / 100;
  const scaledDimension = `${(100 / zoomScale).toFixed(2)}%`;

  return (
    <Box onMouseDownCapture={collapseDrawer}>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Cargando CRM asignados...</Typography>
        </Box>
      )}

      {error && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
      )}

      {!loading && !hasCrmTabs && !error && (
        <Alert severity="info">
          No se encontraron skills con CRM configurado para tu usuario.
        </Alert>
      )}

      {hasCrmTabs && (
        <Paper
          elevation={2}
          sx={{
            p: '10px',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 120px)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                flex: 1,
                minHeight: 24,
                '& .MuiTabs-flexContainer': {
                  alignItems: 'center'
                },
                '& .MuiTab-root': {
                  minHeight: 20,
                  padding: '2px 8px',
                  fontSize: '0.8rem'
                }
              }}
            >
              {crmTabs.map((tab, index) => (
                <Tab
                  key={tab.skillId}
                  label={`${tab.skillname}${tab.crmName ? ` - ${tab.crmName}` : ''}`}
                  id={`crm-tab-${index}`}
                  aria-controls={`crm-panel-${index}`}
                />
              ))}
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                select
                size="small"
                label="Zoom"
                value={zoomPercent}
                onChange={(e) => setZoomPercent(Number(e.target.value))}
                sx={{ width: 90 }}
              >
                {[80, 90, 100, 110, 120, 130].map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}%</MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  height: 32,
                  minWidth: 110,
                  fontSize: '0.8rem',
                  padding: '0 12px'
                }}
              >
                Actualizar
              </Button>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, mt: '5px', display: 'flex', flexDirection: 'column' }}>
            {crmTabs.map((tab, index) => (
              <TabPanel key={tab.skillId} value={activeTab} index={index}>
                {tab.error ? (
                  <Alert severity="error">{tab.error}</Alert>
                ) : (
                  <Box sx={{ flexGrow: 1, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    <iframe
                      title={`CRM-${tab.skillname}`}
                      src={tab.iframeSrc}
                      onLoad={() => handleIframeLoad(tab.skillId)}
                      onMouseDownCapture={collapseDrawer}
                      onFocus={collapseDrawer}
                      style={{
                        width: scaledDimension,
                        height: scaledDimension,
                        border: 0,
                        transform: `scale(${zoomScale})`,
                        transformOrigin: 'top left'
                      }}
                      allow="clipboard-write; clipboard-read; geolocation"
                    />
                  </Box>
                )}
              </TabPanel>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DashboardPage;
